import { useEffect, useMemo, useState } from "react";
import Button from "components/Button";
import {
  Modal,
  Box,
  Typography,
  Checkbox,
  Select,
  MenuItem,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { connect } from "react-redux";
import moment from "moment";
import IndicatorsActioner from "services/indicators/actioner";
import { useIntl } from "react-intl";
import { Close } from "@mui/icons-material";
const DownloadDataModal = ({
  from,
  to,
  selectedIndicators,
  boSettingsIndicators,
  categories,
}) => {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const [selectedItems, setselectedItems] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [xAxis, setXAxis] = useState("DAY");
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const calendarTypeOptions = useMemo(
    () => [
      {
        label: intl.messages["indicatorsScene.modal.totalData"],
        value: "NONE",
      },
      {
        label: intl.messages["indicatorsScene.modal.dailyData"],
        value: "DAY",
      },
      {
        label: intl.messages["indicatorsScene.modal.hourlyData"],
        value: "HOUR",
      },
    ],
    [intl]
  );

  useEffect(() => {
    setselectedItems(
      boSettingsIndicators.map((indicator, index) => {
        return {
          ...indicator,
          activated: selectedIndicators[index]?.checked ?? false,
          items: indicator.items.map((item, indexItem) => {
            return {
              ...item,
              activated:
                selectedIndicators[index]?.areasChecked[indexItem] !== undefined
                  ? true
                  : false,
            };
          }),
        };
      })
    );
  }, [boSettingsIndicators, selectedIndicators]);

  useEffect(() => {
    setXAxis(moment(from).isSame(moment(to), "day") ? "HOUR" : "DAY");
  }, [from, to]);

  const categoriesOptions = useMemo(() => {
    return categories.map((category) => ({
      label:
        intl.messages[
          `indicatorsScene.table.header.${category
            .replace("nb_", "")
            .toLowerCase()}`
        ],
      value: category,
    }));
  }, [categories, intl]);

  const handleIndicatorChecked = (index, activated) => {
    const newSelectedItems = structuredClone(selectedItems);
    newSelectedItems[index].activated = activated;
    if (activated) {
      newSelectedItems[index].items = newSelectedItems[index].items.map(
        (item) => ({
          ...item,
          activated: false,
        })
      );
    }
    setselectedItems(newSelectedItems);
  };

  const handleItemChecked = (indexIndicator, indexItem, activated) => {
    const newSelectedItems = structuredClone(selectedItems);
    if (activated) {
      newSelectedItems[indexIndicator].activated = false;
    }
    newSelectedItems[indexIndicator].items[indexItem].activated = activated;
    setselectedItems(newSelectedItems);
  };

  const handleDownload = async () => {
    setLoading(true);
    const objectToSend = {
      config: selectedItems,
      categories: selectedCategories,
      categories_translations: categoriesOptions.filter((category) =>
        selectedCategories.includes(category.value)
      ),
      from: moment(from).startOf("day").unix(),
      to: moment(to).endOf("day").unix(),
      x_axis: xAxis,
    };
    await IndicatorsActioner.downloadIndicators(
      objectToSend,
      `indicators-${moment(from).format("YYYY-MM-DD")}-${moment(to).format(
        "YYYY-MM-DD"
      )}`
    );
    setLoading(false);
  };

  const hasAreasChecked = useMemo(() => {
    return selectedItems.some(
      (indicator) =>
        indicator.activated || indicator.items.some((item) => item.activated)
    );
  }, [selectedItems]);

  return (
    <>
      <Button
        fullWidth={false}
        label={intl.formatMessage({
          id: "indicatorsScene.modal.btnDownload",
        })}
        onClick={handleOpen}
        style={{ marginRight: "10px" }}
      />
      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "50%",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            maxHeight: "80vh",
            overflow: "auto",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography id="modal-modal-title" variant="h6" component="h2">
              {intl.formatMessage(
                {
                  id: "indicatorsScene.modal.title",
                },
                {
                  from: moment(from).format("DD MMMM YYYY"),
                  to: moment(to).format("DD MMMM YYYY"),
                }
              )}
            </Typography>
            <IconButton onClick={handleClose} size="small">
              <Close />
            </IconButton>
          </Box>
          <Typography id="modal-modal-description" sx={{ mt: 2 }}>
            {intl.formatMessage({
              id: "indicatorsScene.modal.selectIndicators",
            })}
          </Typography>
          {selectedItems.map((indicator, indexIndicator) => (
            <Box key={indexIndicator.toString()}>
              <Box style={{ display: "flex", alignItems: "center" }}>
                <Checkbox
                  size="small"
                  checked={indicator.activated}
                  onChange={(e) =>
                    handleIndicatorChecked(indexIndicator, e.target.checked)
                  }
                />
                <Typography
                  key={indexIndicator.toString()}
                  id="modal-modal-description"
                  sx={{ mt: 0 }}
                >
                  {indicator.title}
                </Typography>
              </Box>
              <Box
                style={{
                  display: "flex",
                  flexDirection: "column",
                  paddingLeft: "40px",
                }}
              >
                {indicator.items.map((item, indexItem) => (
                  <Box
                    key={indexItem.toString()}
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <Checkbox
                      size="small"
                      checked={item.activated}
                      onChange={(e) =>
                        handleItemChecked(
                          indexIndicator,
                          indexItem,
                          e.target.checked
                        )
                      }
                    />
                    <Typography
                      key={indexItem.toString()}
                      id="modal-modal-description"
                      sx={{ mt: 0 }}
                    >
                      {item.subtitle}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
          <Box>
            <Typography id="modal-modal-description" sx={{ mt: 2, mb: 2 }}>
              {intl.formatMessage({
                id: "indicatorsScene.modal.selectCategories",
              })}
            </Typography>
            <Box>
              <Select
                multiple
                value={selectedCategories}
                style={{ width: "100%", height: "40px" }}
                onChange={(e) => setSelectedCategories(e.target.value)}
                renderValue={(selected) =>
                  selected
                    .map(
                      (value) =>
                        categoriesOptions.find((cat) => cat.value === value)
                          ?.label
                    )
                    .join(", ")
                }
              >
                {categoriesOptions.map((category, index) => (
                  <MenuItem key={index} value={category.value}>
                    <Checkbox
                      checked={selectedCategories.indexOf(category.value) > -1}
                    />
                    {category.label}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          </Box>
          <Box>
            <Typography id="modal-modal-description" sx={{ mt: 2, mb: 2 }}>
              {intl.formatMessage({
                id: "indicatorsScene.modal.fileFormat",
              })}
            </Typography>
            <Select
              value={xAxis}
              onChange={(e) => setXAxis(e.target.value)}
              style={{ width: "100%", height: "40px" }}
            >
              {calendarTypeOptions.map((option, index) => (
                <MenuItem key={index} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            {loading ? (
              <CircularProgress />
            ) : (
              <Button
                fullWidth={false}
                label={intl.formatMessage({
                  id: "indicatorsScene.modal.download",
                })}
                onClick={handleDownload}
                disabled={
                  selectedCategories.length === 0 || !hasAreasChecked || loading
                }
              />
            )}
          </Box>
        </Box>
      </Modal>
    </>
  );
};

const mapStateToProps = (state) => ({
  boSettingsIndicators: state.Indicators.indicators,
  from: state.Indicators.filters.from,
  to: state.Indicators.filters.to,
});

export default connect(mapStateToProps)(DownloadDataModal);
